import { Link } from "react-router-dom";
import { Phone, Mail, ExternalLink } from "lucide-react";
import { EDGE, EDGE_LOGO, EDGE_NAVY, EDGE_APP_URL } from "../../lib/edge";
import MortgageDisclosure from "../../components/MortgageDisclosure";
import Seo from "../../components/Seo";

const PIERRE_PHOTO =
  "https://customer-assets.emergentagent.com/job_tx-property-hub/artifacts/q73pz95s_JosephPierre-129cropped-hires.jpg";

export default function LoanOfficers() {
  return (
    <div className="bg-stone-50 min-h-screen" data-testid="loan-officers-page">
      <Seo
        title="Loan Officers"
        description={`Meet Pierre Joseph, NMLS #${EDGE.officer.nmls} — Loan Officer at Edge Home Finance and Realtor at Gens Realty.`}
        path="/loan-officers"
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: EDGE_NAVY }}>
          Edge Home Finance · Loan Officer
        </div>
        <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A] max-w-3xl">
          One person. <em className="not-italic" style={{ color: EDGE_NAVY }}>Both sides</em> of the table.
        </h1>
        <p className="text-[#555] mt-5 max-w-2xl text-lg">
          Pierre is a licensed Texas Realtor with Gens Realty AND a licensed Loan Officer with Edge Home Finance —
          rare alignment that means tighter coordination and fewer surprises.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 aspect-square lg:aspect-auto bg-stone-100 overflow-hidden">
            <img
              src={PIERRE_PHOTO}
              alt={EDGE.officer.name}
              loading="eager"
              decoding="sync"
              className="w-full h-full object-cover object-top"
              style={{
                imageRendering: "auto",
                WebkitBackfaceVisibility: "hidden",
                transform: "translateZ(0)",
              }}
            />
          </div>
          <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="rounded p-1.5" style={{ background: EDGE_NAVY }}>
                <img src={EDGE_LOGO} alt="Edge Home Finance" className="h-7 w-auto" />
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">
                Edge Home Finance · NMLS #{EDGE.nmls}
              </div>
            </div>
            <h2 className="font-display text-4xl font-semibold tracking-tight text-[#1A1A1A]">
              {EDGE.officer.name}
            </h2>
            <div className="text-sm font-medium mt-1" style={{ color: EDGE_NAVY }}>
              Loan Officer · NMLS #{EDGE.officer.nmls}
            </div>
            <div className="text-sm text-gray-500">
              Texas Realtor #{EDGE.officer.realtorNumber} · Gens Realty, LLC
            </div>

            <div className="mt-6 space-y-4 text-[#333] leading-relaxed">
              <p>
                Pierre Joseph is uniquely positioned to serve Houston home buyers — combining the on-the-ground
                Houston market expertise of a licensed Texas Realtor with the financing depth of a licensed Loan Officer.
              </p>
              <p>
                Whether you're a first-time buyer, moving up, refinancing, or relocating to Houston from out-of-state,
                Pierre walks you through the entire journey: budget, programs, property search, offer, lock, close.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
              <a
                href={`tel:${EDGE.officer.phone.replace(/\D/g, "")}`}
                className="flex items-center gap-3 bg-stone-50 border border-gray-200 hover:border-[#E63946] rounded-md px-4 py-3 text-sm font-medium"
                data-testid="lo-phone"
              >
                <Phone className="w-4 h-4 text-[#E63946]" /> {EDGE.officer.phone}
              </a>
              <a
                href={`mailto:${EDGE.officer.email}`}
                className="flex items-center gap-3 bg-stone-50 border border-gray-200 hover:border-[#E63946] rounded-md px-4 py-3 text-sm font-medium"
                data-testid="lo-email"
              >
                <Mail className="w-4 h-4 text-[#E63946]" /> {EDGE.officer.email}
              </a>
            </div>

            <div className="mt-auto pt-8 flex flex-wrap gap-3">
              <a
                href={EDGE_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2"
                data-testid="lo-cta-preapproval"
              >
                Apply Online (1003) <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <MortgageDisclosure />
    </div>
  );
}
